import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { useAuthStore } from "../store/authStore";
import { useBreakpoint } from "../hooks/useBreakpoint";
import { useToast, ToastView } from "../components/ui";
import { StatusIcon, STATUS_COLORS } from "../components/StatusIcon";

const TYPE_LABELS: Record<string, string> = {
  new_entry: "Nueva entrada",
  price_change: "Cambio precio",
  reimbursement_change: "Financiación",
  exclusion: "Exclusión",
  deduction: "Deducción",
  warning: "Aviso",
  marketing_status: "Comercialización",
  eu_registration: "Registro europeo",
  regulation: "Normativa",
  exclusivity: "Exclusividad",
};

// v3 liquid glass: el color de tipo vive SOLO en el punto semántico.
const TYPE_COLORS: Record<string, string> = {
  new_entry: "#60a5fa",
  price_change: "#f59e0b",
  reimbursement_change: "#0891b2",
  exclusion: "#f87171",
  deduction: "#a78bfa",
  warning: "#fb923c",
  marketing_status: "#f472b6",
  eu_registration: "#38bdf8",
  regulation: "#a78bfa",
  exclusivity: "#a855f7",
};

type RadarSummary = {
  month_label: string;
  kpis: Record<string, number>;
  lanes: Record<string, { label: string; description: string; color: string }>;
  total_events: number;
};

type SourceStatus = {
  id: number;
  name: string;
  code: string;
  is_active: boolean;
  status: string;
  last_checked_at: string | null;
  last_successful_at: string | null;
  frequency: string;
  next_run: string | null;
};

type OrgStat = {
  org_id: string;
  org_name: string;
  admin_user_id: string | null;
  plan: string | null;
  portfolio_changes: number;
  competitor_changes: number;
  total_relevant: number;
};

const PLAN_BADGE: Record<string, { label: string; bg: string; color: string }> = {
  core:   { label: "Core",  bg: "#3b82f620", color: "#3b82f6" },
  basic:  { label: "Core",  bg: "#3b82f620", color: "#3b82f6" },
  pro:    { label: "Pro",   bg: "#8b5cf620", color: "#8b5cf6" },
  ultra:  { label: "Ultra", bg: "#f59e0b20", color: "#f59e0b" },
};

type RecentChange = {
  id: string;
  change_type: string;
  product_name: string | null;
  cn_code: string | null;
  laboratory: string | null;
  detected_at: string | null;
  relevance_score: number | null;
  source_name: string;
  affects_orgs: string[];
};

type LabStat = {
  laboratory: string;
  count: number;
  last_at: string | null;
};

type GlobalDashboard = {
  changes_today: number;
  changes_week: number;
  changes_month: number;
  total_changes: number;
  by_lab: LabStat[];
  by_source: Array<{ source_id: number; source_name: string; count: number; last_at: string | null }>;
  by_type: Record<string, number>;
  by_org: OrgStat[];
  recent_changes: RecentChange[];
  sources_status: SourceStatus[];
};

// ── Estilos base v3 ─────────────────────────────────────────────────────────

const panel: React.CSSProperties = {
  background: "var(--clr-surface)",
  border: "1px solid var(--clr-border)",
  borderRadius: 18,
  backdropFilter: "var(--clr-blur)",
  WebkitBackdropFilter: "var(--clr-blur)",
  boxShadow: "var(--clr-shadow-sm)",
};

function ArrowUpRightIcon({ size = 11 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.6} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M7 17 17 7M9 7h8v8" />
    </svg>
  );
}

function ChevronIcon({ dir }: { dir: "left" | "right" }) {
  return (
    <svg width={14} height={14} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.6} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      {dir === "left" ? <path d="m15 18-6-6 6-6" /> : <path d="m9 18 6-6-6-6" />}
    </svg>
  );
}

function WarningIcon({ size = 28 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.6} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
      <path d="M12 9v4M12 17h.01" />
    </svg>
  );
}

function relativeTime(iso: string | null): string {
  if (!iso) return "—";
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 2) return "hace un momento";
  if (mins < 60) return `hace ${mins} min`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `hace ${hours} h`;
  const days = Math.floor(hours / 24);
  return `hace ${days} d`;
}

const LAB_PAGE_SIZE = 20;

export default function SuperAdminGlobalDashboardPage() {
  const startImpersonation = useAuthStore(s => s.startImpersonation);
  const navigate = useNavigate();
  const [data, setData] = useState<GlobalDashboard | null>(null);
  const [error, setError] = useState("");
  const [retryKey, setRetryKey] = useState(0);
  const [labSearch, setLabSearch] = useState("");
  const [labPage, setLabPage] = useState(1);
  const [impersonatingId, setImpersonatingId] = useState<string | null>(null);
  const { isMobile, isTablet } = useBreakpoint();
  const { toast, showToast } = useToast();

  const handleEnterOrg = async (userId: string) => {
    setImpersonatingId(userId);
    try {
      const d = await api.post<{
        access_token: string; refresh_token: string; role: string; email: string;
        first_name: string | null; last_name: string | null;
        organization_id: string | null; organization_name: string | null; plan: string | null;
        primary_color: string | null; logo_url: string | null;
      }>(`/auth/impersonate/${userId}`, {});
      startImpersonation(d.access_token, d.refresh_token, {
        email: d.email, role: d.role,
        first_name: d.first_name, last_name: d.last_name,
        organization_id: d.organization_id, organization_name: d.organization_name,
        plan: d.plan, primary_color: d.primary_color, logo_url: d.logo_url,
      });
      navigate("/dashboard");
    } catch (e: any) {
      showToast(`Error al entrar: ${e.message}`, false);
    } finally {
      setImpersonatingId(null);
    }
  };

  const [radar, setRadar] = useState<RadarSummary | null>(null);

  useEffect(() => {
    api.get<GlobalDashboard>("/superadmin/global-dashboard")
      .then(setData)
      .catch(e => setError(e.message));
    // Franja regulatoria: se alimenta del MISMO endpoint que la página Radar, así
    // sus números no pueden divergir de los que ves al hacer clic (§75).
    api.get<RadarSummary>("/radar").then(setRadar).catch(() => {});
  }, [retryKey]);

  if (error) return (
    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: 64, gap: 16, color: "var(--clr-text)" }}>
      <span style={{ color: "var(--clr-text3)" }}><WarningIcon /></span>
      <div style={{ fontSize: "1rem", fontWeight: 600, color: "var(--clr-text)" }}>No se pudo cargar el dashboard global</div>
      <div style={{ fontSize: "0.82rem", color: "var(--clr-text3)", maxWidth: 400, textAlign: "center" }}>
        {error}
      </div>
      <button
        onClick={() => { setError(""); setData(null); setRetryKey(k => k + 1); }}
        style={{ background: "var(--clr-org-primary)", color: "#fff", border: 0, borderRadius: 999, padding: "0.5rem 1.3rem", fontSize: "0.82rem", fontWeight: 600, cursor: "pointer", marginTop: 8 }}
      >
        Reintentar
      </button>
    </div>
  );

  if (!data) return (
    <div style={{ color: "var(--clr-text4)", padding: 32, fontSize: "0.85rem" }}>Cargando dashboard global…</div>
  );

  // Los KPI cuentan SOLO movimientos de producto — exactamente lo que enseña
  // /changes — así que basta con mandar el instante EXACTO para que el número del
  // KPI y el de la lista coincidan (una fecha sin hora arrastraría horas de más).
  // Los eventos regulatorios viven en la franja de abajo y en el Radar.
  const drill = (msAgo: number | null, label: string) => () => {
    const p = new URLSearchParams({ label });
    if (msAgo !== null) p.set("from_date", new Date(Date.now() - msAgo).toISOString());
    navigate(`/changes?${p}`);
  };
  const HOUR = 3600e3, DAY = 24 * HOUR;

  // Mismo patrón que las cajas del Radar: número arriba, etiqueta debajo. La
  // etiqueta fusiona el antiguo título + ventana temporal en una sola línea.
  const kpis: Array<{ label: string; value: number; onClick: () => void }> = [
    {
      label: "Eventos últimas 24 h", value: data.changes_today,
      onClick: drill(24 * HOUR, "Últimas 24 h"),
    },
    {
      label: "Eventos últimos 7 días", value: data.changes_week,
      onClick: drill(7 * DAY, "Últimos 7 días"),
    },
    {
      label: "Eventos últimos 30 días", value: data.changes_month,
      onClick: drill(30 * DAY, "Últimos 30 días"),
    },
    {
      label: "Total acumulado desde el inicio", value: data.total_changes,
      onClick: drill(null, "Todos los eventos"),
    },
  ];

  return (
    <div style={{ padding: "1.75rem 2rem 2.5rem", maxWidth: 1400, margin: "0 auto", color: "var(--clr-text)", fontVariantNumeric: "tabular-nums" }}>
      <ToastView toast={toast} />
      <h1 style={{ margin: "0 0 0.25rem", fontSize: "1.4rem", fontWeight: 700, letterSpacing: "-0.02em", color: "var(--clr-text)" }}>
        Panel de Control
      </h1>
      <p style={{ margin: "0 0 1.75rem", fontSize: "0.82rem", color: "var(--clr-text3)" }}>
        Vista superadmin, sin filtro por organización: movimientos de producto y, debajo,
        el pulso regulatorio del mes.
      </p>

      {/* ── KPIs: un solo panel de vidrio con franja de datos ───────────── */}
      {/* Cabecera simétrica a la del Radar: hace explícita la dualidad —arriba
          movimientos de PRODUCTO, abajo eventos regulatorios (§75). Sin periodo:
          cada KPI tiene su propia ventana (hoy / 7d / 30d / total). */}
      <div style={{ ...panel, padding: "1.1rem 1.4rem", marginBottom: 28 }}>
        <div style={{ marginBottom: "0.9rem" }}>
          <span style={{ fontSize: "0.7rem", fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.9, color: "var(--clr-text4)" }}>
            Movimientos de Producto
          </span>
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))" }}>
        {kpis.map(({ label, value, onClick }, i) => (
          <div
            key={label}
            onClick={onClick}
            style={{
              paddingLeft: i > 0 ? 18 : 0,
              borderLeft: i > 0 ? "1px solid var(--clr-border2)" : "none",
              cursor: "pointer",
            }}
          >
            {/* Número arriba, etiqueta + flecha debajo — simétrico con el Radar. */}
            <div style={{ fontSize: "1.9rem", fontWeight: 700, letterSpacing: "-0.02em", color: "var(--clr-text)", lineHeight: 1.05 }}>
              {value.toLocaleString("es-ES")}
            </div>
            <div style={{ fontSize: "0.64rem", color: "var(--clr-text4)", textTransform: "uppercase", letterSpacing: 0.8, marginTop: 6, display: "flex", alignItems: "center", gap: 5 }}>
              {label}
              <ArrowUpRightIcon />
            </div>
          </div>
        ))}
        </div>
      </div>

      {/* Franja regulatoria del mes, DEBAJO de los KPI: los movimientos de
          producto son el sujeto y el pulso regulatorio, el contexto. Dos
          universos, cada uno con su contador y su destino: un «cambios hoy: 0»
          deja de alarmar si aquí hay actividad. Los números salen del mismo
          endpoint que la página Radar → no pueden divergir. */}
      {radar && (
        <div
          onClick={() => navigate("/radar")}
          title="Ver el Radar Regulatorio"
          style={{
            background: "var(--clr-surface)", border: "1px solid var(--clr-border)",
            borderRadius: 18, padding: "1.1rem 1.4rem", marginBottom: "1.5rem",
            backdropFilter: "var(--clr-blur)", WebkitBackdropFilter: "var(--clr-blur)",
            boxShadow: "var(--clr-shadow-sm)", cursor: "pointer",
          }}>
          <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", marginBottom: "0.9rem", flexWrap: "wrap", gap: 8 }}>
            <span style={{ fontSize: "0.7rem", fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.9, color: "var(--clr-text4)" }}>
              Radar Regulatorio · {radar.month_label}
            </span>
            <span style={{ fontSize: "0.74rem", color: "var(--clr-text4)" }}>
              {radar.total_events.toLocaleString("es-ES")} eventos este mes ↗
            </span>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))", gap: 16 }}>
            {Object.entries(radar.lanes).map(([key, lane]) => (
              <div key={key}>
                <div style={{ fontSize: "1.7rem", fontWeight: 700, color: lane.color, lineHeight: 1.1 }}>
                  {(radar.kpis[key] ?? 0).toLocaleString("es-ES")}
                </div>
                <div style={{ fontSize: "0.68rem", fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.8, color: "var(--clr-text3)", marginTop: 4 }}>
                  {lane.label}
                </div>
                <div style={{ fontSize: "0.7rem", color: "var(--clr-text4)", marginTop: 2 }}>
                  {lane.description}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── Clientes HelixScan ──────────────────────────────────────────── */}
      {data.by_org.length > 0 && (
        <div style={{ marginBottom: 24 }}>
          <div style={{ display: "flex", alignItems: "baseline", gap: 10, marginBottom: 10 }}>
            <span style={{ fontSize: "0.68rem", fontWeight: 600, color: "var(--clr-text4)", textTransform: "uppercase", letterSpacing: 1 }}>
              Organizaciones Activas
            </span>
            <span style={{ fontSize: "0.72rem", color: "var(--clr-text4)" }}>actividad últimos 30 días</span>
          </div>
          {/* Grid auto-fit: las tarjetas nunca comprimen por debajo de 240px
              (con flex se recortaban las métricas y el botón pisaba el nombre) */}
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))", gap: 14 }}>
            {[...data.by_org].sort((a, b) => b.total_relevant - a.total_relevant).map(org => {
              const clickable = !!org.admin_user_id && !impersonatingId;
              const entering = impersonatingId === org.admin_user_id;
              return (
              <div
                key={org.org_id}
                role={org.admin_user_id ? "button" : undefined}
                tabIndex={org.admin_user_id ? 0 : undefined}
                aria-label={org.admin_user_id ? `Entrar en ${org.org_name}` : undefined}
                onClick={() => { if (clickable) handleEnterOrg(org.admin_user_id!); }}
                onKeyDown={e => { if (clickable && (e.key === "Enter" || e.key === " ")) { e.preventDefault(); handleEnterOrg(org.admin_user_id!); } }}
                onMouseEnter={e => { if (clickable) { const el = e.currentTarget as HTMLElement; el.style.background = "var(--clr-hover)"; el.style.boxShadow = "var(--clr-shadow)"; el.style.transform = "translateY(-2px)"; } }}
                onMouseLeave={e => { const el = e.currentTarget as HTMLElement; el.style.background = "var(--clr-surface)"; el.style.boxShadow = "var(--clr-shadow-sm)"; el.style.transform = "translateY(0)"; }}
                style={{
                  ...panel, padding: "1rem 1.15rem",
                  cursor: org.admin_user_id ? (impersonatingId ? "wait" : "pointer") : "default",
                  opacity: entering ? 0.6 : 1,
                  transition: "background 0.15s, box-shadow 0.15s, transform 0.15s",
                }}
              >
                <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 8, marginBottom: 12 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 7, flexWrap: "wrap", minWidth: 0, flex: 1 }}>
                    <span style={{ fontSize: "0.88rem", fontWeight: 600, letterSpacing: "-0.01em", color: "var(--clr-text)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: "100%" }}>
                      {org.org_name}
                    </span>
                    {org.plan && PLAN_BADGE[org.plan] && (
                      <span style={{
                        fontSize: "0.6rem", fontWeight: 700,
                        textTransform: "uppercase", letterSpacing: "0.6px",
                        padding: "2px 7px", borderRadius: 999,
                        background: PLAN_BADGE[org.plan].bg,
                        color: PLAN_BADGE[org.plan].color,
                      }}>
                        {PLAN_BADGE[org.plan].label}
                      </span>
                    )}
                  </div>
                  {org.admin_user_id && (
                    <span style={{ display: "inline-flex", alignItems: "center", color: "var(--clr-org-primary)", flexShrink: 0, marginTop: 2 }} aria-hidden>
                      {entering
                        ? <span style={{ fontSize: "0.7rem", fontWeight: 600 }}>Entrando…</span>
                        : <ArrowUpRightIcon size={16} />}
                    </span>
                  )}
                </div>
                {/* Franja de métricas: números en tinta con separadores finos */}
                <div style={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)", borderTop: "1px solid var(--clr-border2)" }}>
                  {[
                    { label: "Portfolio",    value: org.portfolio_changes },
                    { label: "Competidores", value: org.competitor_changes },
                  ].map(({ label, value }, i) => (
                    <div key={label} style={{
                      padding: "10px 6px 0",
                      textAlign: "center",
                      borderLeft: i > 0 ? "1px solid var(--clr-border2)" : "none",
                    }}>
                      <div style={{ fontSize: "1.25rem", fontWeight: 700, letterSpacing: "-0.02em", color: "var(--clr-text)", lineHeight: 1 }}>
                        {value.toLocaleString("es-ES")}
                      </div>
                      <div style={{ fontSize: "0.62rem", color: "var(--clr-text4)", marginTop: 4, textTransform: "uppercase", letterSpacing: 0.8 }}>{label}</div>
                    </div>
                  ))}
                </div>
              </div>
              );
            })}
          </div>
        </div>
      )}

      {/* ── Two-column: labs + sources ──────────────────────────────────── */}
      <div style={{ display: "grid", gridTemplateColumns: isMobile || isTablet ? "1fr" : "1fr 320px", gap: 20, marginBottom: 24 }}>

        {/* Todos los laboratorios del mercado */}
        {(() => {
          const filtered = (data.by_lab || []).filter(l =>
            !labSearch || l.laboratory.toLowerCase().includes(labSearch.toLowerCase())
          );
          const totalPages = Math.ceil(filtered.length / LAB_PAGE_SIZE);
          const page = Math.min(labPage, totalPages || 1);
          const visible = filtered.slice((page - 1) * LAB_PAGE_SIZE, page * LAB_PAGE_SIZE);

          return (
            <div style={{ ...panel, overflow: "hidden" }}>
              {/* Header */}
              <div style={{ padding: "1rem 1.25rem", borderBottom: "1px solid var(--clr-border2)", display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
                <div style={{ flex: 1 }}>
                  <span style={{ fontSize: "0.85rem", fontWeight: 600, color: "var(--clr-text)" }}>
                    Laboratorios del mercado
                  </span>
                  <span style={{ marginLeft: 8, fontSize: "0.72rem", color: "var(--clr-text4)" }}>
                    {(data.by_lab || []).length} laboratorios · {data.total_changes.toLocaleString("es-ES")} eventos en totales
                  </span>
                </div>
                <input
                  type="text"
                  placeholder="Buscar laboratorio…"
                  value={labSearch}
                  onChange={e => { setLabSearch(e.target.value); setLabPage(1); }}
                  style={{
                    padding: "0.42rem 1rem", borderRadius: 999, border: "1px solid var(--clr-border)",
                    background: "var(--clr-input-bg)", color: "var(--clr-text)", fontSize: "0.78rem",
                    outline: "none", width: 200,
                  }}
                />
              </div>

              {/* Table */}
              <div style={{ overflowX: "auto" }}>
                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "0.82rem" }}>
                  <thead>
                    <tr style={{ textAlign: "left" }}>
                      {["Laboratorio", "Eventos detectados", "Última actualización"].map(h => (
                        <th key={h} style={{
                          padding: "8px 16px", fontSize: "0.65rem", fontWeight: 600,
                          textTransform: "uppercase", letterSpacing: 0.9,
                          color: "var(--clr-text4)", whiteSpace: "nowrap",
                        }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {visible.length === 0 && (
                      <tr>
                        <td colSpan={3} style={{ padding: 24, textAlign: "center", color: "var(--clr-text4)" }}>
                          No se encontraron laboratorios
                        </td>
                      </tr>
                    )}
                    {visible.map(lab => (
                      <tr
                        key={lab.laboratory}
                        style={{ borderTop: "1px solid var(--clr-border2)", transition: "background 0.1s" }}
                        onMouseEnter={e => (e.currentTarget as HTMLTableRowElement).style.background = "var(--clr-hover)"}
                        onMouseLeave={e => (e.currentTarget as HTMLTableRowElement).style.background = ""}
                      >
                        <td style={{ padding: "9px 16px" }}>
                          <button
                            onClick={() => navigate(`/changes?laboratory=${encodeURIComponent(lab.laboratory)}`)}
                            title="Ver todos los eventos de este laboratorio"
                            style={{
                              background: "transparent", border: "none", padding: 0,
                              cursor: "pointer", color: "var(--clr-text)",
                              fontSize: "0.82rem", fontWeight: 600,
                              display: "inline-flex", alignItems: "center", gap: 5,
                            }}
                          >
                            {lab.laboratory}
                            <span style={{ color: "var(--clr-text4)", display: "inline-flex" }}><ArrowUpRightIcon /></span>
                          </button>
                        </td>
                        <td style={{ padding: "9px 16px", fontWeight: 600, color: "var(--clr-text)" }}>
                          {lab.count.toLocaleString("es-ES")}
                        </td>
                        <td style={{ padding: "9px 16px", color: "var(--clr-text4)", fontSize: "0.75rem", whiteSpace: "nowrap" }}>
                          {relativeTime(lab.last_at)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div style={{ padding: "10px 16px", borderTop: "1px solid var(--clr-border2)", display: "flex", alignItems: "center", justifyContent: "space-between", fontSize: "0.75rem", color: "var(--clr-text4)" }}>
                  <span>{filtered.length} resultados · pág {page} de {totalPages}</span>
                  <div style={{ display: "flex", gap: 6 }}>
                    <button
                      onClick={() => setLabPage(p => Math.max(1, p - 1))}
                      disabled={page === 1}
                      aria-label="Página anterior"
                      style={{
                        width: 28, height: 28, borderRadius: 999, border: "1px solid var(--clr-border)",
                        background: "var(--clr-surface)", color: "var(--clr-text2)", padding: 0,
                        display: "inline-flex", alignItems: "center", justifyContent: "center",
                        cursor: page === 1 ? "default" : "pointer", opacity: page === 1 ? 0.4 : 1,
                      }}
                    >
                      <ChevronIcon dir="left" />
                    </button>
                    <button
                      onClick={() => setLabPage(p => Math.min(totalPages, p + 1))}
                      disabled={page === totalPages}
                      aria-label="Página siguiente"
                      style={{
                        width: 28, height: 28, borderRadius: 999, border: "1px solid var(--clr-border)",
                        background: "var(--clr-surface)", color: "var(--clr-text2)", padding: 0,
                        display: "inline-flex", alignItems: "center", justifyContent: "center",
                        cursor: page === totalPages ? "default" : "pointer", opacity: page === totalPages ? 0.4 : 1,
                      }}
                    >
                      <ChevronIcon dir="right" />
                    </button>
                  </div>
                </div>
              )}
            </div>
          );
        })()}

        {/* Estado de fuentes */}
        <div style={{ ...panel, overflow: "hidden" }}>
          <div style={{ padding: "1rem 1.25rem", borderBottom: "1px solid var(--clr-border2)", fontSize: "0.85rem", fontWeight: 600, color: "var(--clr-text)" }}>
            Estado de fuentes
          </div>
          <div style={{ padding: "0.5rem 0" }}>
            {data.sources_status.map((s, i) => {
              const dotColor = STATUS_COLORS[s.status] ?? STATUS_COLORS.inactive;
              const label = s.status === "ok" ? "OK" : s.status === "warning" ? "Aviso" : s.status === "error" ? "Error" : s.status === "inactive" ? "Inactiva" : "Pendiente";
              const isLast = i === data.sources_status.length - 1;
              return (
                <div key={s.id} style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 16px", borderBottom: isLast ? "none" : "1px solid var(--clr-border2)", opacity: s.is_active ? 1 : 0.45 }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: "0.8rem", fontWeight: 500, color: "var(--clr-text)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {s.name}
                    </div>
                    {!s.is_active ? (
                      <div style={{ fontSize: "0.7rem", color: "var(--clr-text4)" }}>Fuente desactivada</div>
                    ) : (
                      <div style={{ fontSize: "0.68rem", color: "var(--clr-text4)", display: "flex", gap: 10, flexWrap: "wrap", marginTop: 2 }}>
                        <span>
                          <span style={{ opacity: 0.7 }}>Última:</span>{" "}
                          {s.last_checked_at
                            ? new Date(s.last_checked_at).toLocaleString("es-ES", { dateStyle: "short", timeStyle: "short" })
                            : "—"}
                        </span>
                        {s.next_run && (
                          <span>
                            <span style={{ opacity: 0.7 }}>Próxima:</span>{" "}
                            {s.next_run}
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                  <span style={{
                    display: "inline-flex", alignItems: "center", gap: 6,
                    background: dotColor + "1e", color: dotColor,
                    borderRadius: 999, padding: "3px 11px",
                    fontSize: "0.72rem", fontWeight: 600, whiteSpace: "nowrap",
                  }}>
                    <StatusIcon status={s.status} size={9} />
                    {label}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* ── Recent changes table (full-width) ──────────────────────────── */}
      <div style={{ ...panel, overflow: "hidden" }}>
        <div style={{ padding: "1rem 1.25rem", borderBottom: "1px solid var(--clr-border2)" }}>
          <span style={{ fontSize: "0.85rem", fontWeight: 600, color: "var(--clr-text)" }}>
            Últimos eventos (global)
          </span>
          <span style={{ marginLeft: 8, fontSize: "0.72rem", color: "var(--clr-text4)" }}>sin filtro por organización</span>
        </div>
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "0.82rem" }}>
            <thead>
              <tr style={{ textAlign: "left" }}>
                {["Producto", "Tipo de cambio", "Fuente", "Afecta a", "Detectado"].map(h => (
                  <th key={h} style={{
                    padding: "8px 14px", fontSize: "0.65rem", fontWeight: 600,
                    textTransform: "uppercase", letterSpacing: 0.9,
                    color: "var(--clr-text4)", whiteSpace: "nowrap",
                  }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {data.recent_changes.length === 0 && (
                <tr>
                  <td colSpan={5} style={{ padding: 24, textAlign: "center", color: "var(--clr-text4)" }}>
                    Sin eventos detectados
                  </td>
                </tr>
              )}
              {data.recent_changes.map(c => {
                const typeColor = TYPE_COLORS[c.change_type] ?? "#94a3b8";
                return (
                  <tr key={c.id} style={{ borderTop: "1px solid var(--clr-border2)" }}>
                    {/* Producto */}
                    <td style={{ padding: "10px 14px", maxWidth: 240 }}>
                      <div style={{ fontWeight: 500, color: "var(--clr-text)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                        {c.product_name || "—"}
                      </div>
                    </td>
                    {/* Tipo */}
                    <td style={{ padding: "10px 14px", whiteSpace: "nowrap" }}>
                      {/* Tipo = cápsula tintada suave SIN punto (el punto es de estados) */}
                      <span style={{
                        display: "inline-flex", alignItems: "center",
                        background: typeColor + "1e", color: typeColor,
                        borderRadius: 999, padding: "3px 11px",
                        fontSize: "0.75rem", fontWeight: 600,
                      }}>
                        {TYPE_LABELS[c.change_type] ?? c.change_type}
                      </span>
                    </td>
                    {/* Fuente */}
                    <td style={{ padding: "10px 14px", color: "var(--clr-text4)", whiteSpace: "nowrap", fontSize: "0.78rem" }}>
                      {c.source_name}
                    </td>
                    {/* Afecta a */}
                    <td style={{ padding: "10px 14px", minWidth: 160 }}>
                      {c.affects_orgs.length === 0 ? (
                        <span style={{ color: "var(--clr-text4)", fontSize: "0.75rem" }}>Ninguna</span>
                      ) : (
                        <span style={{ fontSize: "0.75rem", fontWeight: 500, color: "var(--clr-text3)" }}>
                          {c.affects_orgs.join(" · ")}
                        </span>
                      )}
                    </td>
                    {/* Detectado */}
                    <td style={{ padding: "10px 14px", color: "var(--clr-text4)", whiteSpace: "nowrap", fontSize: "0.75rem" }}>
                      {relativeTime(c.detected_at)}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
